import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import axiosInstance from '../utils/axiosConfig';
import { getActionErrorMessage } from '../utils/errorHandler';
import { normalizeUsers } from '../utils/userUtils';
import '../styles/AdminPreceptorAvailabilityManager.css';
import LoadingSpinner from './LoadingSpinner';
import { MATCH_BY_INSTITUTION, getCurriculumMatchBy, getPreceptorEntityIds } from '../utils/rankUtils';

const AdminPreceptorAvailabilityManager = () => {
    const [preceptors, setPreceptors] = useState([]);
    const [selectedPreceptor, setSelectedPreceptor] = useState(null);
    const [preceptorAvailability, setPreceptorAvailability] = useState(Array(8).fill(0));
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [currentYear, setCurrentYear] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSpecialities, setFilterSpecialities] = useState([]);
    const [filterInstitutions, setFilterInstitutions] = useState([]);
    const [filterCurriculums, setFilterCurriculums] = useState([]);
    const [specialities, setSpecialities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [dataSource, setDataSource] = useState('none'); // 'existing', 'api', or 'none'
    const [curriculums, setCurriculums] = useState([]);
    const [ranks, setRanks] = useState([]);
    const [selectedCurriculumId, setSelectedCurriculumId] = useState('');
    // Speciality id when the curriculum's rank matches by experience, institution
    // id when it matches by institution.
    const [selectedEntityId, setSelectedEntityId] = useState('');
    const [configurations, setConfigurations] = useState({}); // curriculum_id -> config (rotations, etc.)

    const getRotationCountForCurriculum = (curriculumId) => {
        const key = curriculumId != null ? String(curriculumId) : '';
        const config = key ? (configurations[key] ?? null) : null;
        const fromRotations = Array.isArray(config?.rotations) ? config.rotations.length : 0;
        const fromCount = parseInt(config?.rotationsCount, 10);
        const fromNumber = parseInt(config?.rotationsNumber, 10);
        return Math.max(1, fromRotations || fromCount || fromNumber || 8);
    };

    const normalizeAvailabilityArray = (availability, requiredLength) => {
        const source = Array.isArray(availability) ? availability : [];
        return Array.from({ length: requiredLength }, (_, index) => {
            const raw = index < source.length ? source[index] : 0;
            const parsed = parseInt(raw, 10);
            return Math.max(0, Math.min(2, Number.isNaN(parsed) ? 0 : parsed));
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            setFetchLoading(true);
            setError('');
            try {
                const now = new Date();
                const curriculumYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
                setCurrentYear(curriculumYear);

                // Fetch all data in parallel
                const [preceptorsResponse, specialitiesRes, institutionsRes, curriculaRes, ranksRes] = await Promise.all([
                    axiosInstance.get('/crud/get_all_preceptors'),
                    axiosInstance.get('/crud/get_all_specialities').catch(() => ({ data: [] })),
                    axiosInstance.get('/crud/get_all_institutions').catch(() => ({ data: [] })),
                    axiosInstance.get('/curriculum/curriculums').catch(() => ({ data: [] })),
                    axiosInstance.get('/ranks/ranks').catch(() => ({ data: [] }))
                ]);

                setRanks(Array.isArray(ranksRes.data) ? ranksRes.data : []);

                // Process preceptors
                const preceptorsData = Array.isArray(preceptorsResponse.data) ? preceptorsResponse.data : [];
                setPreceptors(normalizeUsers(preceptorsData));

                // Process specialities
                const specRaw = Array.isArray(specialitiesRes?.data) ? specialitiesRes.data : [];
                setSpecialities(specRaw.map(s => ({
                    _id: s._id ?? s.id,
                    name: s.name ?? s.speciality ?? String(s._id ?? s.id ?? '')
                })));

                // Process institutions
                const instRaw = Array.isArray(institutionsRes?.data) ? institutionsRes.data : [];
                setInstitutions(instRaw.map(i => ({
                    _id: i._id ?? i.id,
                    name: i.name ?? String(i._id ?? i.id ?? '')
                })));

                // Process curricula
                const curriculaRaw = Array.isArray(curriculaRes?.data) ? curriculaRes.data : [];
                const normalizedCurricula = curriculaRaw
                    .map(c => ({
                        _id: String(c?._id ?? ''),
                        name: String(c?.name ?? c?._id ?? '').trim(),
                        rankId: c?.rankId ?? null,
                        rank: c?.rank ?? null
                    }))
                    .filter(c => c._id && c.name);
                setCurriculums(normalizedCurricula);

                // Batch fetch configs for each curriculum
                const configPromises = curriculaRaw.map(curr =>
                    axiosInstance.get(`/config/get_configuration_by_curriculum/${curr._id}`)
                        .then(res => ({ currId: curr._id, config: Array.isArray(res.data) ? res.data[0] : res.data }))
                        .catch(() => ({ currId: curr._id, config: null }))
                );
                const configResults = await Promise.all(configPromises);
                
                const configsByCurriculum = {};
                configResults.forEach(r => {
                    if (r.config) {
                        configsByCurriculum[String(r.currId)] = r.config;
                    }
                });
                setConfigurations(configsByCurriculum);

                setSelectedCurriculumId('');
            } catch (err) {
                setError(err.response?.data?.detail || 'Failed to fetch data');
                setTimeout(() => setError(''), 3000);
            } finally {
                setFetchLoading(false);
            }
        };

        fetchData();
    }, []);

    const curriculumKey = selectedCurriculumId || (currentYear != null ? String(currentYear) : null);

    // What the currently selected curriculum's rank matches on: 'experience' or 'institution'.
    const getCurriculumById = (curriculumId) => curriculums.find(c => String(c._id) === String(curriculumId));
    const selectedMatchBy = getCurriculumMatchBy(ranks, getCurriculumById(selectedCurriculumId));

    // Resolve the availability list from the nested structure: ra[currId][entityId]
    const extractAvailabilityList = (ra, currId, entityId) => {
        if (!ra || typeof ra !== 'object' || Array.isArray(ra)) return null;
        const currLevel = ra[currId];
        if (!currLevel) return null;
        if (Array.isArray(currLevel)) return currLevel;  // old flat format fallback
        if (typeof currLevel === 'object') {
            if (entityId && currLevel[entityId]) return currLevel[entityId];
            // No entityId: return the first entity's list
            const first = Object.values(currLevel)[0];
            return Array.isArray(first) ? first : null;
        }
        return null;
    };

    const fetchPreceptorAvailability = async (preceptorId, curriculumId, entityId) => {
        const key = curriculumId != null ? String(curriculumId) : curriculumKey;
        const requiredLength = getRotationCountForCurriculum(key);
        setLoading(true);
        try {
            const res = await axiosInstance.get('/crud/get_preceptor_availability', {
                params: { preceptor_id: preceptorId }
            });
            const raw = res.data;
            const list = extractAvailabilityList(raw, key, entityId);
            setPreceptorAvailability(normalizeAvailabilityArray(list, requiredLength));
        } catch (err) {
            console.error('Error fetching preceptor availability:', err);
            setPreceptorAvailability(Array(requiredLength).fill(0));
        } finally {
            setLoading(false);
        }
    };

    // Load availability for the selected curriculum+entity from local preceptor data or API
    const loadAvailabilityForCurriculumEntity = (preceptor, curriculumId, entityId) => {
        const key = curriculumId != null ? String(curriculumId) : curriculumKey;
        const requiredLength = getRotationCountForCurriculum(key);
        const ra = preceptor?.rotationsAvailabilities;
        const list = extractAvailabilityList(ra, key, entityId);
        if (Array.isArray(list)) {
            setPreceptorAvailability(normalizeAvailabilityArray(list, requiredLength));
            setDataSource('existing');
        } else if (preceptor?._id) {
            setDataSource('api');
            fetchPreceptorAvailability(preceptor._id, curriculumId, entityId);
        } else {
            setPreceptorAvailability(Array(requiredLength).fill(0));
        }
    };

    // Curriculum IDs present in the preceptor's rotationsAvailabilities that also exist in our curriculum list
    const getPreceptorCurriculumIds = (preceptor) => {
        const ra = preceptor?.rotationsAvailabilities;
        if (!ra || typeof ra !== 'object' || Array.isArray(ra)) return [];
        return Object.keys(ra);
    };

    const getKnownPreceptorCurriculumIds = (preceptor) => {
        const known = new Set(curriculums.map(c => String(c._id)));
        return getPreceptorCurriculumIds(preceptor).filter(id => known.has(String(id)));
    };

    // Specialty IDs the preceptor has (from specialityIds field, falling back to specialityId)
    const getPreceptorSpecialityIds = (preceptor) => {
        const ids = preceptor?.specialityIds;
        if (Array.isArray(ids) && ids.length > 0) return ids.map(String);
        const single = preceptor?.specialityId;
        return single ? [String(single)] : [];
    };

    // Institution IDs the preceptor has (from institutionIds, falling back to
    // legacy institutionId) — a preceptor can hold several, same as specialities.
    const getPreceptorInstitutionIds = (preceptor) => getPreceptorEntityIds(preceptor, MATCH_BY_INSTITUTION);

    // Entity ids (specialities or institutions) the preceptor can set availability for
    // on a given curriculum, per that curriculum rank's matchBy.
    const getPreceptorEntityIdsForCurriculum = (preceptor, curriculumId) => {
        const matchBy = getCurriculumMatchBy(ranks, getCurriculumById(curriculumId));
        return getPreceptorEntityIds(preceptor, matchBy);
    };

    const handlePreceptorSelect = async (preceptor) => {
        setSelectedPreceptor(preceptor);
        const currIds = getKnownPreceptorCurriculumIds(preceptor);
        const lastCurrId = currIds.length > 0 ? currIds[currIds.length - 1] : '';
        const entityIds = lastCurrId ? getPreceptorEntityIdsForCurriculum(preceptor, lastCurrId) : [];
        const firstEntityId = entityIds.length > 0 ? entityIds[0] : '';
        setSelectedCurriculumId(lastCurrId);
        setSelectedEntityId(firstEntityId);
        if (lastCurrId && firstEntityId) {
            loadAvailabilityForCurriculumEntity(preceptor, lastCurrId, firstEntityId);
        } else {
            setPreceptorAvailability(Array(8).fill(0));
        }
    };

    const handleCurriculumChange = (e) => {
        const id = e.target.value;
        setSelectedCurriculumId(id);
        // Switching curricula can switch the matching entity type — re-derive it.
        const entityIds = selectedPreceptor ? getPreceptorEntityIdsForCurriculum(selectedPreceptor, id) : [];
        const nextEntityId = entityIds[0] || '';
        setSelectedEntityId(nextEntityId);
        if (id && selectedPreceptor && nextEntityId) {
            loadAvailabilityForCurriculumEntity(selectedPreceptor, id, nextEntityId);
        }
    };

    const handleEntityChange = (e) => {
        const id = e.target.value;
        setSelectedEntityId(id);
        if (id && selectedPreceptor && selectedCurriculumId) {
            loadAvailabilityForCurriculumEntity(selectedPreceptor, selectedCurriculumId, id);
        }
    };

    // Config for the currently selected curriculum (from pre-fetched configurations)
    const config = selectedCurriculumId ? (configurations[selectedCurriculumId] ?? configurations[String(selectedCurriculumId)] ?? null) : null;
    const selectedRotationCount = getRotationCountForCurriculum(selectedCurriculumId || curriculumKey);
    const displayAvailability = normalizeAvailabilityArray(preceptorAvailability, selectedRotationCount);

    // Config rotation dates are normalized ISO strings (e.g. "2025-09-01T00:00:00.000Z")
    const getConfigDate = (dateObj) => {
        if (!dateObj) return null;
        if (typeof dateObj === 'string') return dateObj;
        if (dateObj instanceof Date) return dateObj;
        if (typeof dateObj === 'number') return new Date(dateObj);
        return null;
    };

    const handleAvailabilityChange = (rotationIndex, value) => {
        const newValue = Math.max(0, Math.min(2, parseInt(value) || 0));
        const newAvailability = [...displayAvailability];
        newAvailability[rotationIndex] = newValue;
        setPreceptorAvailability(newAvailability);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedPreceptor) {
            setError('Please select a preceptor first');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        
        try {
            if (!selectedEntityId) {
                setError(`Please select a${selectedMatchBy === MATCH_BY_INSTITUTION ? 'n institution' : ' speciality'} before saving.`);
                return;
            }

            const validAvailability = normalizeAvailabilityArray(preceptorAvailability, selectedRotationCount).map(val => {
                const numVal = parseInt(val);
                if (isNaN(numVal) || numVal < 0 || numVal > 2) {
                    throw new Error(`Invalid availability value: ${val}. Must be 0, 1, or 2.`);
                }
                return numVal;
            });

            const curriculumId = curriculumKey || (currentYear != null ? String(currentYear) : (() => { const n = new Date(); return String(n.getMonth() >= 8 ? n.getFullYear() : n.getFullYear() - 1); })());

            await axiosInstance.put('/crud/admin/preceptor/availability', null, {
                params: {
                    preceptor_id: selectedPreceptor._id,
                    curriculum_id: curriculumId,
                    entity_id: selectedEntityId,
                    new_availability: JSON.stringify(validAvailability)
                }
            });

            setSuccess(`Availability updated successfully for ${selectedPreceptor.firstname} ${selectedPreceptor.lastname}!`);
            setTimeout(() => setSuccess(''), 3000);

            // Merge the new slots into the local nested structure
            const mergeAvail = (prev) => {
                const next = prev && typeof prev === 'object' && !Array.isArray(prev) ? { ...prev } : {};
                const currLevel = next[curriculumId] && typeof next[curriculumId] === 'object' && !Array.isArray(next[curriculumId])
                    ? { ...next[curriculumId] }
                    : {};
                currLevel[selectedEntityId] = validAvailability;
                next[curriculumId] = currLevel;
                return next;
            };
            setPreceptors(prevPreceptors =>
                prevPreceptors.map(p =>
                    p._id === selectedPreceptor._id
                        ? { ...p, rotationsAvailabilities: mergeAvail(p.rotationsAvailabilities) }
                        : p
                )
            );
            setSelectedPreceptor(prev => ({
                ...prev,
                rotationsAvailabilities: mergeAvail(prev.rotationsAvailabilities)
            }));

            setDataSource('existing');
            
        } catch (err) {
            console.error('Availability update error:', err);
            if (err.response?.status === 422) {
                setError('Data validation error. Please check the availability values (must be 0, 1, or 2).');
            } else {
                setError(getActionErrorMessage(err, "update availability"));
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateInput) => {
        if (!dateInput) return '—';
        if (typeof dateInput === 'string' && dateInput.toLowerCase().includes('invalid date')) return '—';
        const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(d.getTime())) return '—';
        const rendered = d.toLocaleDateString();
        return rendered.toLowerCase().includes('invalid date') ? '—' : rendered;
    };

    // Informational only — unlike the preceptor's own My Availability page,
    // admins can still edit availability while registration is closed.
    const isWithinRegistration = (curriculumConfig) => {
        const now = new Date();
        const regStart = new Date(curriculumConfig?.form_registration?.startDate);
        const regEnd = new Date(curriculumConfig?.form_registration?.endDate);
        const validDates = !isNaN(regStart.getTime()) && !isNaN(regEnd.getTime());
        return validDates && now >= regStart && now <= regEnd;
    };

    const getPreceptorFullName = (preceptor) => {
        return `${preceptor.firstname || ''} ${preceptor.lastname || ''}`.trim();
    };

    const getPreceptorDisplayInfo = (preceptor) => {
        const fullName = getPreceptorFullName(preceptor);
        const specIds = getPreceptorSpecialityIds(preceptor);
        const specialityNames = specIds
            .map(id => specialities.find(s => String(s._id) === id)?.name)
            .filter(Boolean);
        const speciality = specialityNames.length > 0 ? specialityNames.join(', ') : 'N/A';
        const instIds = getPreceptorInstitutionIds(preceptor);
        const institutionNames = instIds
            .map(id => institutions.find(i => String(i._id) === id)?.name)
            .filter(Boolean);
        const institution = institutionNames.length > 0 ? institutionNames.join(', ') : 'N/A';
        const email = preceptor.email || 'N/A';
        return { fullName, speciality, institution, email };
    };

    // Preceptor must have at least one selected curriculum present in BOTH currentCurriculums and rotationsAvailabilities keys
    const preceptorMatchesCurriculumFilter = (preceptor) => {
        if (!filterCurriculums || filterCurriculums.length === 0) return true;
        const currentCurriculums = preceptor.currentCurriculums;
        const currentIds = Array.isArray(currentCurriculums)
            ? currentCurriculums.map(c => (c && (c._id ?? c))).filter(Boolean).map(String)
            : [];
        const ra = preceptor.rotationsAvailabilities;
        const raKeys = ra && typeof ra === 'object' && !Array.isArray(ra) ? Object.keys(ra) : [];
        const currentSet = new Set(currentIds);
        const raSet = new Set(raKeys);
        const hasAtLeastOneInBoth = filterCurriculums.some(
            id => currentSet.has(String(id)) && raSet.has(String(id))
        );
        return hasAtLeastOneInBoth;
    };

    const filteredPreceptors = preceptors.filter(preceptor => {
        const { fullName, email } = getPreceptorDisplayInfo(preceptor);
        const matchesSearch = fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            email.toLowerCase().includes(searchTerm.toLowerCase());
        // Match any of the preceptor's specialityIds
        const preceptorSpecIds = getPreceptorSpecialityIds(preceptor);
        const matchesSpeciality = filterSpecialities.length === 0 ||
            filterSpecialities.some(id => preceptorSpecIds.includes(String(id)));
        const preceptorInstIds = getPreceptorInstitutionIds(preceptor);
        const matchesInstitution = filterInstitutions.length === 0 ||
            filterInstitutions.some(id => preceptorInstIds.includes(String(id)));
        const matchesCurriculum = preceptorMatchesCurriculumFilter(preceptor);
        return matchesSearch && matchesSpeciality && matchesInstitution && matchesCurriculum;
    });

    const getAvailabilityStatus = (value) => {
        if (value === 0) return { text: 'Unavailable', class: 'unavailable' };
        if (value === 1) return { text: 'Single Student', class: 'single' };
        if (value === 2) return { text: 'Two Students', class: 'double' };
        return { text: 'Unknown', class: 'unknown' };
    };

    return (
        <div className="universal-white-container admin-preceptor-availability-manager">
            <div className="modern-schedule-header">
                <h2>Preceptor Availability Manager</h2>
                <p>Manage and update preceptor availability for student rotations</p>
            </div>

            {/* Success/Error Messages */}
            {success && (
                <div className="alert alert-success">
                    <span className="alert-icon">✅</span>
                    {success}
                </div>
            )}

            {error && (
                <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    {error}
                </div>
            )}

            <div className="tab-content">
                <div className="create-form-section">
                    <h3 className="section-title">Preceptor Selection</h3>
                    
                    {/* Search and Filters */}
                    <div className={`search-filters ${fetchLoading ? 'search-filters--loading' : ''}`}>
                        {fetchLoading && (
                            <div className="search-filters-loading" aria-live="polite">
                                <LoadingSpinner text="Loading preceptors..." variant="inline" size="small" />
                            </div>
                        )}
                        <div className="apam-search-box">
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="apam-search-input"
                                disabled={fetchLoading}
                                aria-busy={fetchLoading}
                            />
                            <span className="apam-search-icon">🔍</span>
                        </div>
                        
                        <div className="filter-stack">
                            <div className="filter-row">
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    isDisabled={fetchLoading || curriculums.length === 0}
                                    value={curriculums.filter(c => filterCurriculums.includes(String(c._id))).map(c => ({
                                        value: String(c._id),
                                        label: c.name || c._id
                                    }))}
                                    onChange={(selected) => {
                                        setFilterCurriculums(selected ? selected.map(opt => opt.value) : []);
                                    }}
                                    options={curriculums.map(c => ({
                                        value: String(c._id),
                                        label: c.name || c._id
                                    }))}
                                    placeholder={fetchLoading ? 'Loading…' : (curriculums.length === 0 ? 'No Curricula found' : 'All Curricula')}
                                    noOptionsMessage={() => 'No Curricula found'}
                                    classNamePrefix="curriculum-filter-select"
                                    styles={{
                                        container: base => ({ ...base, width: '100%' }),
                                        control: base => ({
                                            ...base,
                                            minHeight: 44,
                                            padding: '0 14px',
                                            fontSize: '0.95rem',
                                            opacity: fetchLoading ? 0.7 : 1
                                        }),
                                        placeholder: base => ({ ...base, color: '#64748b', fontSize: '0.95rem' })
                                    }}
                                />
                            </div>
                            <div className="filter-row">
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    isDisabled={fetchLoading}
                                    value={specialities.filter(s => filterSpecialities.includes(String(s._id))).map(s => ({
                                        value: String(s._id),
                                        label: s.name || 'Unknown'
                                    }))}
                                    onChange={(selected) => {
                                        setFilterSpecialities(selected ? selected.map(opt => opt.value) : []);
                                    }}
                                    options={specialities.map(s => ({
                                        value: String(s._id),
                                        label: s.name || 'Unknown'
                                    }))}
                                    placeholder={fetchLoading ? 'Loading…' : 'All Rotation Types'}
                                    classNamePrefix="speciality-filter-select"
                                    styles={{
                                        container: base => ({ ...base, width: '100%' }),
                                        control: base => ({
                                            ...base,
                                            minHeight: 44,
                                            padding: '0 14px',
                                            fontSize: '0.95rem',
                                            opacity: fetchLoading ? 0.7 : 1
                                        }),
                                        placeholder: base => ({ ...base, color: '#64748b', fontSize: '0.95rem' })
                                    }}
                                />
                            </div>
                            <div className="filter-row">
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    isDisabled={fetchLoading}
                                    value={institutions.filter(i => filterInstitutions.includes(String(i._id))).map(i => ({
                                        value: String(i._id),
                                        label: i.name || 'Unknown'
                                    }))}
                                    onChange={(selected) => {
                                        setFilterInstitutions(selected ? selected.map(opt => opt.value) : []);
                                    }}
                                    options={institutions.map(i => ({
                                        value: String(i._id),
                                        label: i.name || 'Unknown'
                                    }))}
                                    placeholder={fetchLoading ? 'Loading…' : 'All Institutions'}
                                    classNamePrefix="institution-filter-select"
                                    styles={{
                                        container: base => ({ ...base, width: '100%' }),
                                        control: base => ({
                                            ...base,
                                            minHeight: 44,
                                            padding: '0 14px',
                                            fontSize: '0.95rem',
                                            opacity: fetchLoading ? 0.7 : 1
                                        }),
                                        placeholder: base => ({ ...base, color: '#64748b', fontSize: '0.95rem' })
                                    }}
                                />
                            </div>
                            <div className="filter-row">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFilterSpecialities([]);
                                        setFilterInstitutions([]);
                                        setFilterCurriculums([]);
                                        setSearchTerm('');
                                    }}
                                    className="filter-reset-btn"
                                    disabled={fetchLoading}
                                >
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Preceptors List */}
                    <div className="preceptors-list">
                        <h4>Available Preceptors ({filteredPreceptors.length})</h4>
                        
                        {fetchLoading ? (
                            <div className="loading-state">
                                <LoadingSpinner text="Loading preceptors..." variant="inline" />
                            </div>
                        ) : (
                            <div className="preceptors-grid">
                                {filteredPreceptors.map((preceptor) => {
                                    const { fullName, speciality, institution, email } = getPreceptorDisplayInfo(preceptor);
                                    const isSelected = selectedPreceptor?._id === preceptor._id;

                                    return (
                                        <div
                                            key={preceptor._id}
                                            className={`preceptor-card ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handlePreceptorSelect(preceptor)}
                                        >
                                            <div className="preceptor-avatar">
                                                {fullName.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="preceptor-info">
                                                <h5 className="preceptor-name">{fullName}</h5>
                                                <p className="preceptor-speciality">{speciality}</p>
                                                <p className="preceptor-email">{email}</p>
                                                <p className="preceptor-institution">{institution}</p>
                                            </div>
                                            <div className="selection-indicator">
                                                {isSelected ? '✓' : '→'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="list-section">
                    <h3 className="section-title">
                        {selectedPreceptor ? `Availability Management - ${getPreceptorFullName(selectedPreceptor)}` : 'Select a Preceptor'}
                    </h3>
                    
                    {!selectedPreceptor ? (
                        <div className="no-selection-message">
                            <div className="no-selection-icon">👤</div>
                            <h4>No Preceptor Selected</h4>
                            <p>Please select a preceptor from the list to manage their availability.</p>
                        </div>
                    ) : (
                        <>
                            <div className="selected-preceptor-card">
                                <div className="selected-preceptor-card-inner">
                                    <div className="selected-preceptor-avatar">
                                        {getPreceptorFullName(selectedPreceptor).charAt(0).toUpperCase()}
                                    </div>
                                    <div className="selected-preceptor-fields">
                                        <div className="selected-preceptor-field">
                                            <span className="selected-preceptor-field-label">Name</span>
                                            <span className="selected-preceptor-field-value">{getPreceptorFullName(selectedPreceptor)}</span>
                                        </div>
                                        <div className="selected-preceptor-field">
                                            <span className="selected-preceptor-field-label">Rotation Type(s)</span>
                                            <span className="selected-preceptor-field-value">
                                                {getPreceptorSpecialityIds(selectedPreceptor)
                                                    .map(id => specialities.find(s => String(s._id) === id)?.name || id)
                                                    .join(', ') || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="selected-preceptor-field">
                                            <span className="selected-preceptor-field-label">Email</span>
                                            <span className="selected-preceptor-field-value">{selectedPreceptor.email || 'N/A'}</span>
                                        </div>
                                        <div className="selected-preceptor-field">
                                            <span className="selected-preceptor-field-label">Institution(s)</span>
                                            <span className="selected-preceptor-field-value">
                                                {getPreceptorInstitutionIds(selectedPreceptor)
                                                    .map(id => institutions.find(i => String(i._id) === id)?.name || id)
                                                    .join(', ') || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="selected-preceptor-field selected-preceptor-field--curriculum">
                                            <span className="selected-preceptor-field-label">Curriculum to edit</span>
                                            <select
                                                value={selectedCurriculumId}
                                                onChange={handleCurriculumChange}
                                                className="curriculum-select"
                                            >
                                                {(() => {
                                                    const preceptorCurriculumIds = getKnownPreceptorCurriculumIds(selectedPreceptor);
                                                    if (preceptorCurriculumIds.length === 0) {
                                                        return <option value="">No Curricula found</option>;
                                                    }
                                                    return preceptorCurriculumIds.slice().sort((a, b) => b.localeCompare(a)).map(cid => {
                                                        const label = curriculums.find(c => String(c._id) === cid)?.name || '';
                                                        return (
                                                            <option key={cid} value={String(cid)}>
                                                                {label || cid}
                                                            </option>
                                                        );
                                                    });
                                                })()}
                                            </select>
                                        </div>
                                        <div className="selected-preceptor-field selected-preceptor-field--curriculum">
                                            <span className="selected-preceptor-field-label">
                                                {selectedMatchBy === MATCH_BY_INSTITUTION ? 'Institution to edit' : 'Rotation type to edit'}
                                            </span>
                                            <select
                                                value={selectedEntityId}
                                                onChange={handleEntityChange}
                                                className="curriculum-select"
                                            >
                                                {(() => {
                                                    const entityIds = getPreceptorEntityIdsForCurriculum(selectedPreceptor, selectedCurriculumId);
                                                    const lookup = selectedMatchBy === MATCH_BY_INSTITUTION ? institutions : specialities;
                                                    if (entityIds.length === 0) {
                                                        return <option value="">{selectedMatchBy === MATCH_BY_INSTITUTION ? 'No institutions found' : 'No specialities found'}</option>;
                                                    }
                                                    return entityIds.map(eid => {
                                                        const name = lookup.find(item => String(item._id) === eid)?.name || eid;
                                                        return (
                                                            <option key={eid} value={eid}>{name}</option>
                                                        );
                                                    });
                                                })()}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {selectedCurriculumId && (
                                <div className={`registration-range ${isWithinRegistration(config) ? 'open' : 'closed'}`}>
                                    <span className="registration-period-label">Registration Period</span>
                                    {config?.form_registration ? (
                                        <span>
                                            {formatDate(config.form_registration?.startDate)} - {formatDate(config.form_registration?.endDate)}
                                            {' '}({isWithinRegistration(config) ? 'Open for preceptors' : 'Closed for preceptors'} — admins can still edit)
                                        </span>
                                    ) : 'No dates set'}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="items-grid">
                                    {displayAvailability.map((value, index) => (
                                        <div key={index} className="modern-item-card availability-card">
                                            <div className="item-preview">
                                                <div className="item-header">
                                                    <div className="item-main-info">
                                                        <h4 className="item-name">Rotation {index + 1}</h4>
                                                        <div className="rotation-dates">
                                                            <span className="date-label">Period:</span>
                                                            <span className="date-value">
                                                                {config?.rotations?.[index]
                                                                    ? `${formatDate(getConfigDate(config.rotations[index].startDate))} – ${formatDate(getConfigDate(config.rotations[index].endDate))}`
                                                                    : '—'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="availability-input-section">
                                                    <label htmlFor={`rotation-${index}`} className="input-label">
                                                        Update Availability:
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            id={`rotation-${index}`}
                                                            type="number"
                                                            min="0"
                                                            max="2"
                                                            value={value}
                                                            onChange={(e) => handleAvailabilityChange(index, e.target.value)}
                                                            className="availability-input"
                                                        />
                                                        <div className="input-helper">
                                                            <span className="helper-text">0-2 students</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={loading || !selectedCurriculumId || !selectedEntityId}
                                >
                                    {loading ? 'Updating Availability...' : 'Save Availability Changes'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminPreceptorAvailabilityManager;
